<template>
  <app-modal
    v-model="showScheduleModal"
    :disabled="isLoading"
    confirm-text="Yes"
    cancel-text="No"
    @confirm="handleConfirm"
    @cancel="showScheduleModal = false"
  >
    <template #title> Schedule </template>
    <p>
      Is <b>{{ formattedDistributionDate }}</b> your desired distribution date?
    </p>
  </app-modal>

  <app-modal
    class="daily-distribution-modal"
    v-model="showBeforeThreePmNotificationModal"
    :hide-actions="true"
  >
    <template #title> Daily Distribution Cutoff Notification </template>
    <p>
      Same-day distribution orders must be received before 3 p.m. ET; otherwise,
      they will distribute the next business day.
    </p>

    <p class="notice">
      Immediate distribution is available for $150.00. We strive to distribute
      your release within 2 hours (4 hours if Photo Embed or YouTube Embed - is
      selected below).
    </p>

    <div class="countdown">
      Time until 3 p.m ET: {{ leftTimeTicker.minutes }}:{{
        leftTimeTicker.seconds
      }}

      <app-button
        @click="onHideBeforeThreePmNotification"
        theme="primary"
        size="small"
        class="midnight-modal-button"
        >OK</app-button
      >
    </div>
  </app-modal>

  <app-modal v-model="showBeforeMidnightNotificationModal" :hide-actions="true">
    <template #title> Daily Distribution Cutoff Notification </template>

    <div>
      <p>
        Orders for distribution next-business day must be received before
        midnight ET; otherwise, an additional same-day distribution fee will be
        assessed.
      </p>
      <br />
      <br />

      <p class="midnight-modal-timestamp">
        Time until midnight ET: {{ leftTimeTicker.minutes }}:{{
          leftTimeTicker.seconds
        }}

        <app-button
          @click="onHideBeforeMidnightModal"
          theme="primary"
          size="small"
          class="midnight-modal-button"
          >OK</app-button
        >
      </p>
    </div>
  </app-modal>

  <div class="app-step-two-tabs-schedule">
    <div class="app-step-two-tabs__wrapper">
      <div class="app-step-two-tabs-schedule__body">
        <div class="app-step-two-tabs-schedule__line"></div>
        <div class="app-step-two-tabs-schedule__content">
          <h4 class="app-step-two-tabs-schedule__title">
            Schedule Your Press Release
          </h4>

          <div class="app-step-two-tabs-schedule-calendar">
            <div class="app-step-two-tabs-schedule-calendar__col">
              <LoadingBar v-if="isBlackoutDatesLoading" />
              <date-picker
                :columns="columns"
                :attributes="[
                  {
                    key: 'today',
                    dates: moment.tz('America/New_York').format('YYYY-MM-DD'),
                    highlight: {
                      contentClass: 'highlight-today',
                    },
                  },
                ]"
                :model-config="{
                  type: 'string',
                  mask: 'YYYY-MM-DD',
                }"
                timezone="UTC"
                :disabled-dates="[
                  ...blackoutDates,
                  {
                    repeat: {
                      weekdays: [1, 7],
                    },
                  },
                ]"
                :min-date="minDatePickerDate"
                :max-date="maxDatePickerDate"
                @update:modelValue="onSelect"
                :modelValue="distributionDate"
              ></date-picker>
            </div>
          </div>

          <div
            v-if="distributionDate"
            class="app-step-two-tabs-schedule__release"
          >
            <span>Release Date:</span
            ><span> {{ moment(distributionDate).format('YYYY-MM-DD') }} </span>

            <div class="app-step-two-tabs-schedule__release-info">
              We strive to distribute all releases before 9am ET the day of
              distribution.
            </div>
          </div>

          <div
            v-if="isSameDistributionDate"
            class="app-step-two-tabs-schedule__release-notice"
          >
            Immediate distribution is available for $150.00. We strive to
            distribute your release within 2 hours (4 hours if Photo Embed or
            YouTube Embed - is selected below).
          </div>
        </div>
      </div>
    </div>
    <navigation-buttons
      :disabled-outer-condition="!distributionDate"
      :loading="isLoading"
      @onPreviousButtonClick="handlePreviousClick"
      @onNextButtonClick="handleNextClick"
    ></navigation-buttons>
  </div>
</template>

<script setup>
import 'v-calendar/dist/style.css'
import { computed, ref, watch } from 'vue'
import { DatePicker } from 'v-calendar'
import { useSchedule } from './useSchedule'
import { useOrder } from '@core/composables/order/useOrder'
import { useNavigationButtons } from '@modules/app-steps/step-two/navigation-buttons'
import { useTabsStore } from '@/stores/tabs'
import { storeToRefs } from 'pinia'
import { smallerThanLaptop } from '@/stores/breakpoints'
import { useTime } from './useTime'

import AppModal from '@modules/ui/app-modal.vue'
import NavigationButtons from '@modules/app-steps/step-two/navigation-buttons/index.vue'
import moment from 'moment'
import LoadingBar from '@modules/ui/loading-bar.vue'
import AppButton from '@modules/ui/app-button.vue'
const { useUpdateOrder } = useOrder()
const { isLoading, updateOrder } = useUpdateOrder()
const { navigate } = useNavigationButtons()
const { getFrontendTabBuilder } = storeToRefs(useTabsStore())

const { distributionDate, isSameDistributionDate, onSelect, getBlackoutDates } =
  useSchedule()

const { blackoutDates, isBlackoutDatesLoading, isBlackoutDatesReady } =
  getBlackoutDates()

const {
  showBeforeThreePmNotificationModal,
  showBeforeMidnightNotificationModal,
  leftTimeTicker,
  minDatePickerDate,
  maxDatePickerDate,
  onHideBeforeMidnightModal,
  onHideBeforeThreePmNotification,
} = useTime(onSelect, distributionDate, blackoutDates, isBlackoutDatesReady)

const showScheduleModal = ref(false)
const formattedDistributionDate = computed(() => {
  return moment(distributionDate.value).format('ddd MMM DD YYYY')
})

const columns = computed(() => (smallerThanLaptop.value ? 1 : 2))

const handleConfirm = () => {
  updateOrder(
    {
      ...getFrontendTabBuilder.value(true),
      distributionDate: distributionDate.value,
    },
    () => {
      showScheduleModal.value = false
    }
  )
}

const handlePreviousClick = () =>
  updateOrder({ ...getFrontendTabBuilder.value() }, () => navigate())

const handleNextClick = () => (showScheduleModal.value = true)
watch(
  leftTimeTicker,
  ({ expired }) => {
    const isSelectedSameDate = moment(distributionDate.value).isSame(
      moment.tz('America/New_York').format('YYYY-MM-DD'),
      'day'
    )

    if (!expired || !isSelectedSameDate) return

    distributionDate.value = null
  },
  { immediate: true }
)
</script>
